import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Search, Mic, MicOff, Minus, Plus, Trash2, X, User, History, StickyNote,
  Save, Check, ArrowLeft, Package, AlertTriangle, ChevronDown, Sparkles,
} from "lucide-react";

/** Familia visual de un ítem del carrito: prefijo del nombre antes de " - " (o familia_key). */
const familiaDe = (it) => {
  const p = it?._prod;
  if (!p) return `__${it?.producto_id}`;
  const n = String(p.nombre || "").trim();
  const i = n.lastIndexOf(" - ");
  return (i > 0 ? n.substring(0, i) : (p.familia_key || n)).trim().toUpperCase();
};
/** Mantiene el carrito agrupado por familia (en el orden en que apareció cada familia). */
const agruparCarrito = (lista) => {
  const grupos = new Map();
  for (const it of lista) {
    const k = familiaDe(it);
    if (!grupos.has(k)) grupos.set(k, []);
    grupos.get(k).push(it);
  }
  return [...grupos.values()].flat();
};
import { toast } from "sonner";
import { api, hoyISO } from "@/lib/api";
import { useSuppressCritical } from "@/context/NotifSuppressContext";
import { useAuth } from "@/context/AuthContext";
import { useBackTrap, useBeforeUnloadGuard } from "@/hooks/useBackTrap";
import { esProductoDePrueba } from "@/lib/utils";
import OfflineImg from "@/components/OfflineImg";
import IntelligentOrderDialog from "@/components/IntelligentOrderDialog";
import { getOfflineClientes, getOfflineProductos } from "@/lib/offlineStore";
import { fuzzySearch, normalize as fsNormalize, esConsultaCodigo } from "@/lib/fuzzySearch";
import { sonHermanos, nombreSinColor } from "@/lib/familias";

/**
 * PedidoMovilPage — "Modo Vendedor Móvil" (prototipo rápido).
 *
 * Objetivo: tomar pedidos en la calle a la velocidad del cliente ("ta-ta-ta").
 * UI pensada 100% para celular:
 *   - Cliente arriba (compacto, con historial accesible en 1 tap).
 *   - Buscador gigante (SKU / código / nombre) con resultados tipo lista grande.
 *   - Micrófono para dictado: "A123 doce" / "código 150 veinte".
 *   - Ítems con botones -/+ GRANDES, sin modales.
 *   - Totales flotantes abajo + 2 botones: Guardar Borrador / Finalizar.
 *
 * Mínimo acoplamiento con el modal legacy — usamos la misma API `/pedidos` con
 * `forzar_borrador=true` para el borrador rápido, y POST normal para finalizar.
 */

const fmt = (n) => `$ ${Math.round(Number(n) || 0).toLocaleString("es-CO")}`;

// Normaliza para búsqueda: minúsculas, sin tildes, sin espacios extremos.
const norm = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

export default function PedidoMovilPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Jun/2026: en modo móvil el vendedor está armando pedido — nunca bloquearlo con el modal crítico.
  useSuppressCritical(true);

  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cliente seleccionado + flujo
  const [clienteId, setClienteId] = useState("");
  const [showClientePicker, setShowClientePicker] = useState(false);
  const [cliSearch, setCliSearch] = useState("");

  // Historial del cliente (top productos)
  const [historial, setHistorial] = useState(null);
  const [showHistorial, setShowHistorial] = useState(false);

  // Búsqueda productos + voz
  const [prodSearch, setProdSearch] = useState("");
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const searchInputRef = useRef(null);

  // Carrito
  const [items, setItems] = useState([]); // { producto_id, cantidad, precio_unitario, _prod }
  const [ultimoAgregado, setUltimoAgregado] = useState(null);
  useEffect(() => {
    if (!ultimoAgregado) return;
    const el = document.querySelector(`[data-pid="${ultimoAgregado}"]`);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [ultimoAgregado]);
  const [showIntel, setShowIntel] = useState(false);
  const [saving, setSaving] = useState(false);

  // ---------- Carga inicial ----------
  // Nov/2026: carga independiente por recurso + reintento visible si falla el catálogo.
  const [catalogoError, setCatalogoError] = useState(null);
  const cargarCatalogo = useCallback(async () => {
    setLoading(true); setCatalogoError(null);
    const [rc, rp] = await Promise.allSettled([
      api.get("/clientes"),
      api.get("/productos", { params: { limit: 5000 } }),
    ]);
    const lista = (r) => (r.status === "fulfilled" && Array.isArray(r.value?.data) ? r.value.data : null);
    const clis = lista(rc), prods = lista(rp);
    if (clis) setClientes(clis);
    if (prods) setProductos(prods.filter((x) => !esProductoDePrueba(x)));
    if (!clis || !prods) {
      const [offCli, offProd] = await Promise.all([getOfflineClientes().catch(() => []), getOfflineProductos().catch(() => [])]);
      if (!clis && offCli.length) setClientes(offCli);
      if (!prods && offProd.length) { setProductos(offProd); toast.info(`Productos desde tu cache local (${offProd.length})`); }
      if (!prods && !offProd.length) {
        const why = rp.status === "rejected" ? (rp.reason?.response?.status ? `error ${rp.reason.response.status}` : (rp.reason?.message || "sin respuesta")) : "respuesta inválida";
        setCatalogoError(why);
        toast.error(`No se pudo cargar el catálogo (${why}). Toca "Reintentar".`);
      }
    }
    setLoading(false);
  }, []);
  useEffect(() => { cargarCatalogo(); }, [cargarCatalogo]);

  // Si venimos con ?cliente=xxx, preseleccionar
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cid = params.get("cliente");
    if (cid) setClienteId(cid);
  }, [location.search]);

  const cliente = clientes.find((c) => c.id === clienteId);

  // Historial cuando cambia el cliente
  useEffect(() => {
    if (!clienteId) {
      setHistorial(null);
      return;
    }
    api
      .get(`/clientes/${clienteId}/historial`, { params: { limit: 20, orden_top: "unidades" } })
      .then((r) => setHistorial(r.data))
      .catch(() => setHistorial(null));
  }, [clienteId]);

  // ---------- Pricing simple ----------
  // (versión reducida — el prototipo no aplica listas de precios, solo sug/min base).
  const pricingFor = useCallback((prod) => {
    const sug = prod.precio_sugerido || prod.precio_base || 0;
    const min = prod.precio_minimo || Math.round(sug * 0.9);
    return { sugerido: sug, minimo: min };
  }, []);

  // ---------- Búsqueda de productos (agrupada por familia) ----------
  // Oct/2026: devolvemos GRUPOS (familias) + sueltos (sin familia_key o
  // familia con 1 sola variante). Cada grupo se puede expandir para ver sus
  // variantes. Esto reduce dramáticamente el ruido cuando el vendedor busca
  // "liston" y había 30 colores — ahora ve "Liston Satinado · 30 colores" y
  // elige el color con 1 tap más.
  const [gruposExpandidos, setGruposExpandidos] = useState(new Set()); // familia_key expandidas
  // Oct/2026: cantidades que el vendedor tipeó por variante antes de agregar.
  // Permite "poner 50 directo" sin tener que tocar + cincuenta veces.
  // Clave: variante.id → valor del input (string, puede estar vacío = default 1).
  const [qtyPorVariante, setQtyPorVariante] = useState({});
  const resultados = useMemo(() => {
    if (!prodSearch.trim()) return { grupos: [], sueltos: [], lista: [] };
    // Match fuzzy sobre TODAS las variantes (así "rojo" encuentra la variante
    // roja aunque el nombre de la familia no lo incluya).
    // Feb/2026: strict — todos los tokens deben matchear algo útil.
    const matches = fuzzySearch(
      productos,
      prodSearch,
      ["sku", "codigo", "nombre", "familia_key", "categoria_nombre", "categoria", "descripcion", "color"],
      { limit: 300, boostFields: ["sku", "codigo"], strict: true },
    );
    // Nov/2026: si se buscó un CÓDIGO ("EF26-146"), no se agregan hermanos con otro código.
    const modoCodigo = esConsultaCodigo(prodSearch);
    // Agrupar por familia_key
    const porFamilia = new Map();
    const sueltos = [];
    for (const p of matches) {
      const key = (p.familia_key || "").trim();
      if (!key) {
        sueltos.push(p);
        continue;
      }
      if (!porFamilia.has(key)) porFamilia.set(key, []);
      porFamilia.get(key).push(p);
    }
    const grupos = [];
    for (const [key, variantes] of porFamilia.entries()) {
      // Oct/2026: SIEMPRE expandir con todas las variantes de la familia
      // (no solo las que matcheó la búsqueda). Si buscas "0.7mm" y el
      // negro de 7mm no está en el nombre pero sí pertenece a la familia,
      // debe aparecer igual al expandir el grupo. Esto corrige el bug
      // reportado "colores que la IA no reconoció no aparecen al buscar".
      let variantesCompletas = variantes;
      // Nov/2026: solo hermanos reales (misma familia + misma primera palabra), ver lib/familias.js
      const hermanos = modoCodigo ? [] : productos.filter(
        (x) => (x.familia_key || "").trim() === key && !variantes.some((v) => v.id === x.id) && sonHermanos(variantes[0], x),
      );
      if (hermanos.length > 0) {
        variantesCompletas = [...variantes, ...hermanos];
      }
      // Jun/2026: colores en orden alfabético. Nov/2026: las variantes que coinciden con lo
      // buscado van primero (ej. "papel coreano rojo" → ROJO arriba), luego el resto.
      const _colorDe = (v) => { const n = String(v.nombre || ""); const i = n.lastIndexOf(" - "); return i > 0 ? n.substring(i + 3).trim() : (v.color || n); };
      const _cmpV = (a, b) => _colorDe(a).localeCompare(_colorDe(b), "es", { sensitivity: "base", numeric: true });
      variantesCompletas = hermanos.length > 0 ? [...variantes].sort(_cmpV).concat([...hermanos].sort(_cmpV)) : [...variantesCompletas].sort(_cmpV);
      // Si solo tiene 1 variante en total, lo mandamos como "suelto" para no
      // sobrecargar la UI con grupos de 1.
      if (variantesCompletas.length === 1) {
        sueltos.push(variantesCompletas[0]);
        continue;
      }
      const stockTotal = variantesCompletas.reduce(
        (s, v) => s + Math.max(0, (v.stock_actual || 0) - (v.stock_reservado || 0)),
        0,
      );
      // Nov/2026: nombre y foto del grupo salen de la variante que MEJOR coincide con lo buscado
      // (antes salía la primera alfabética → buscabas "rosa" y el grupo decía "BLANCO").
      const mejor = variantes[0] || variantesCompletas[0];
      const familiaNombre = (mejor.nombre || "").includes(" - ") ? mejor.nombre.split(" - ")[0] : (nombreSinColor(mejor) || mejor.nombre || key);
      const conStock = [mejor, ...variantesCompletas].find(
        (v) => (v.stock_actual || 0) - (v.stock_reservado || 0) > 0,
      );
      const thumb = conStock || mejor;
      grupos.push({
        key,
        nombre: familiaNombre,
        variantes: variantesCompletas,
        stockTotal,
        thumb,
        codigo: mejor.codigo || variantesCompletas[0].codigo || null,
        // Score de la mejor variante del grupo (preservar el ranking fuzzy)
        orden: matches.indexOf(variantes[0]), // Jun/2026: ranking del mejor match (score fuzzy), no cantidad de variantes
      });
    }
    // Ordenar por relevancia del mejor match (el que mejor coincide con lo escrito va primero)
    grupos.sort((a, b) => a.orden - b.orden);
    // Nov/2026: UNA sola lista por relevancia. Antes los "sueltos" (productos sin familia
    // o únicos) salían SIEMPRE después de todos los grupos → "papel coreano azul" quedaba de último.
    const lista = [
      ...grupos.slice(0, 15).map((g) => ({ ...g, tipo: "grupo" })),
      ...sueltos.slice(0, 20).map((p) => ({ tipo: "suelto", key: `s-${p.id}`, p, orden: matches.indexOf(p) })),
    ].sort((a, b) => a.orden - b.orden);
    return { grupos: grupos.slice(0, 15), sueltos: sueltos.slice(0, 20), lista };
  }, [prodSearch, productos]);

  const toggleGrupo = (key) => {
    setGruposExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const tieneResultados =
    resultados.grupos.length > 0 || resultados.sueltos.length > 0;

  // ---------- Agregar al carrito ----------
  const addProduct = useCallback(
    (prod, cantidad = 1) => {
      const disp = (prod.stock_actual || 0) - (prod.stock_reservado || 0);
      if (disp <= 0 && !prod._permitir_sin_stock) {
        toast.error(`${prod.nombre} — sin stock`);
        return;
      }
      const { sugerido } = pricingFor(prod);
      setItems((prev) => {
        const i = prev.findIndex((it) => it.producto_id === prod.id);
        if (i >= 0) {
          const next = [...prev];
          next[i] = { ...next[i], cantidad: next[i].cantidad + cantidad };
          return next;
        }
        return agruparCarrito([
          ...prev,
          {
            producto_id: prod.id,
            cantidad,
            precio_unitario: sugerido,
            _prod: prod,
          },
        ]);
      });
      setUltimoAgregado(prod.id);
    },
    [pricingFor],
  );

  const updateQty = (idx, delta) => {
    setItems((prev) => {
      const next = [...prev];
      const nueva = Math.max(0, (next[idx].cantidad || 0) + delta);
      if (nueva === 0) return next.filter((_, i) => i !== idx);
      next[idx] = { ...next[idx], cantidad: nueva };
      return next;
    });
  };

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  // ---------- Voz: Web Speech API ----------
  // Intenta parsear "A123 doce" / "código 150 20 docenas" / "sku B22 5"
  const WORD_NUMS = {
    cero: 0, uno: 1, una: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5,
    seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10, once: 11, doce: 12,
    trece: 13, catorce: 14, quince: 15, dieciseis: 16, diecisiete: 17,
    dieciocho: 18, diecinueve: 19, veinte: 20, treinta: 30, cuarenta: 40,
    cincuenta: 50, sesenta: 60, setenta: 70, ochenta: 80, noventa: 90, cien: 100,
  };

  const parseVoz = useCallback(
    (texto) => {
      // Oct/2026: devuelve { prod, cantidad } (exacto), { grupos, sueltos, query, cantidad } (match ambiguo)
      // o { query, cantidad: null } (sin match). El flujo:
      //   1. Separa número(s) del transcript (quedan como `cantidad` detectada o null).
      //   2. Resto del texto se usa como query fuzzy sobre el catálogo.
      //   3. Si fuzzy devuelve exactamente 1 resultado → agregar directo.
      //   4. Si devuelve varios → mostrar como búsqueda normal (el user elige).
      const t = norm(texto).replace(/[.,]/g, " ");
      const parts = t.split(/\s+/).filter(Boolean);
      if (parts.length === 0) return { query: "", cantidad: null };

      // Palabras de medida: si un número viene seguido de uno de estos, ES
      // parte del tamaño del producto, NO cantidad (ej. "cinta cuatro centímetros").
      const UNIDADES_MEDIDA = new Set([
        "cm", "centimetro", "centimetros", "centímetro", "centímetros",
        "mm", "milimetro", "milimetros", "milímetro", "milímetros",
        "m", "metro", "metros", "mt", "mts",
        "pulgada", "pulgadas", "pulg", "in",
      ]);
      // Palabras que CONFIRMAN que un número es cantidad
      const UNIDADES_CANTIDAD = new Set([
        "unidad", "unidades", "un", "docena", "docenas", "doc",
        "paca", "pacas", "rollo", "rollos", "paquete", "paquetes", "pqt",
        "caja", "cajas", "bolsa", "bolsas",
      ]);
      // Palabras de ruido que no van al query
      const NOISE = new Set([
        "sku", "codigo", "código", "ref", "referencia", "de", "del", "la",
        "el", "y", "con", "por", "me", "da", "dame", "quiero", "necesito",
        "un", "una", "los", "las",
      ]);

      // Pre-paso: convertir palabras-número a dígitos manteniendo posición.
      // Esto permite que "cuatro centímetros" se convierta a "4 centimetros"
      // (que luego se normaliza a "4cm" para el search).
      const asDigits = parts.map((tok) => {
        if (/^\d+$/.test(tok)) return { tok, num: parseInt(tok, 10) };
        if (WORD_NUMS[tok] !== undefined) return { tok: String(WORD_NUMS[tok]), num: WORD_NUMS[tok] };
        return { tok, num: null };
      });

      // Detectar cantidad: un número cuya próxima palabra es UNIDADES_CANTIDAD,
      // O el ÚLTIMO número de la frase sin medida después.
      let cantidad = null;
      let cantidadIdx = -1;
      // Primera pasada: número + unidad de cantidad explícita (más confiable)
      for (let i = 0; i < asDigits.length - 1; i++) {
        if (asDigits[i].num !== null && UNIDADES_CANTIDAD.has(asDigits[i + 1].tok)) {
          cantidad = asDigits[i].num;
          cantidadIdx = i;
          break;
        }
      }
      // Segunda pasada: el ÚLTIMO número que NO esté seguido de una unidad de medida
      if (cantidad === null) {
        for (let i = asDigits.length - 1; i >= 0; i--) {
          if (asDigits[i].num === null) continue;
          const next = asDigits[i + 1];
          if (next && UNIDADES_MEDIDA.has(next.tok)) continue; // es un tamaño, saltar
          cantidad = asDigits[i].num;
          cantidadIdx = i;
          break;
        }
      }

      // Construir query: todo menos el token-cantidad (y menos ruido)
      const resto = [];
      for (let i = 0; i < asDigits.length; i++) {
        if (i === cantidadIdx) continue;
        // Si es la palabra que sigue a la cantidad y es unidad de cantidad, saltar
        if (i === cantidadIdx + 1 && UNIDADES_CANTIDAD.has(asDigits[i].tok)) continue;
        if (NOISE.has(asDigits[i].tok)) continue;
        resto.push(asDigits[i].tok);
      }

      // Normalizar medidas en el query: "4 centimetros" → "4cm"
      let queryRaw = resto.join(" ").trim();
      queryRaw = queryRaw
        .replace(/(\d+)\s*(centimetros?|centímetros?|cm)/g, "$1cm")
        .replace(/(\d+)\s*(milimetros?|milímetros?|mm)/g, "$1mm")
        .replace(/(\d+)\s*(metros?|mts?)/g, "$1m")
        .replace(/(\d+)\s*(pulgadas?|pulg|in)/g, '$1"');
      const query = queryRaw.trim();
      if (!query) return { query: "", cantidad };

      // Intento 1: match exacto en SKU/código completo
      const exacto = productos.find((p) => {
        const s = norm(p.sku); const c = norm(p.codigo);
        return (s && s === query.replace(/\s+/g, "")) || (c && c === query.replace(/\s+/g, ""));
      });
      if (exacto) return { prod: exacto, cantidad: cantidad || 1 };

      // Intento 2: fuzzy sobre el resto
      const matches = fuzzySearch(
        productos,
        query,
        ["sku", "codigo", "nombre", "familia_key", "categoria_nombre", "categoria", "descripcion", "color"],
        { limit: 10, boostFields: ["sku", "codigo"] },
      );
      if (matches.length === 1) {
        return { prod: matches[0], cantidad: cantidad || 1 };
      }
      // Varios o cero: devolvemos query + cantidad para caer al buscador
      return { query, cantidad, matches };
    },
    [productos],
  );

  const startListening = async () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Tu navegador no soporta dictado por voz. Usa Chrome en Android o Safari en iOS 14.5+.");
      return;
    }
    if (listening) {
      try { recognitionRef.current?.stop(); } catch {}
      setListening(false);
      return;
    }
    // Oct/2026: pedir permiso explícito al mic antes de iniciar (iOS Safari
    // falla silencioso si el usuario nunca lo autorizó).
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Cerramos el stream — solo lo pedimos para disparar el permission prompt
        stream.getTracks().forEach((t) => t.stop());
      }
    } catch (permErr) {
      toast.error("Necesito permiso para el micrófono. Tócale 'Permitir' cuando el navegador te pregunte.", { duration: 6000 });
      return;
    }
    const rec = new SpeechRecognition();
    // Fallback a es-ES si es-CO no está soportado (iOS a veces solo tiene es-ES/es-US)
    rec.lang = "es-ES";
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 3;

    let finalText = "";
    let interimText = "";  // Oct/2026: respaldo si isFinal nunca llega (Safari/iOS)
    const tid = toast.loading("Escuchando… habla ahora", { duration: 20000 });

    rec.onstart = () => {
      setListening(true);
    };
    rec.onerror = (e) => {
      setListening(false);
      toast.dismiss(tid);
      const msgs = {
        "no-speech": "No escuché nada. Intenta de nuevo cerca del micrófono.",
        "audio-capture": "No pude acceder al micrófono.",
        "not-allowed": "Permiso de micrófono denegado. Habilitalo en los ajustes del navegador.",
        "network": "Error de red al procesar la voz.",
        "aborted": null, // silencioso si el usuario cancela
      };
      const m = msgs[e.error];
      if (m) toast.error(m, { duration: 5000 });
      else if (e.error && e.error !== "aborted") toast.error(`Voz: ${e.error}`);
    };
    rec.onend = () => {
      setListening(false);
      toast.dismiss(tid);
      // Oct/2026: si no hubo isFinal, usar interim como fallback. Pasa en
      // Safari iOS y a veces Chrome Android cuando la frase es corta.
      const texto = (finalText.trim() || interimText.trim());
      if (!texto) {
        toast.error("No escuché nada. Intenta otra vez.");
        return;
      }
      const parsed = parseVoz(texto);
      if (parsed.prod) {
        // Match exacto o único: agregar al carrito
        addProduct(parsed.prod, parsed.cantidad || 1);
        toast.success(`+${parsed.cantidad || 1} ${parsed.prod.sku || parsed.prod.nombre}`);
      } else if (parsed.query) {
        // Match ambiguo o cero: caer al buscador textual (resultados agrupados)
        setProdSearch(parsed.query);
        if (parsed.cantidad) {
          toast.info(`"${texto}" → "${parsed.query}" × ${parsed.cantidad} — elige la variante`, { duration: 5000 });
        } else {
          toast.info(`"${texto}" — elige una variante`, { duration: 5000 });
        }
      } else {
        // Último recurso: poner el texto completo en el buscador
        setProdSearch(texto);
        toast.info(`"${texto}" — buscando…`, { duration: 4000 });
      }
    };
    rec.onresult = (ev) => {
      let interimNow = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const alt = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) {
          finalText += " " + alt;
        } else {
          interimNow += alt;
        }
      }
      // Siempre guardamos el último interim como respaldo
      if (interimNow) interimText = interimNow;
      const vista = (finalText + " " + interimNow).trim();
      if (vista) {
        toast.loading(`🎙️ "${vista}"`, { id: tid });
      }
    };
    recognitionRef.current = rec;
    try {
      rec.start();
    } catch (err) {
      setListening(false);
      toast.dismiss(tid);
      toast.error("No pude iniciar el micrófono. Toca otra vez.");
    }
  };

  // ---------- Totales ----------
  const subtotal = useMemo(
    () => items.reduce((s, it) => s + (it.cantidad || 0) * (it.precio_unitario || 0), 0),
    [items],
  );
  const cantidadTotal = useMemo(
    () => items.reduce((s, it) => s + (Number(it.cantidad) || 0), 0),
    [items],
  );

  // Feb/2026 v3 — resumen agrupado por "familia visible": todo lo que
  // está ANTES del primer " - " en el nombre del producto. Combina este
  // enfoque con la estrategia de quitar `color` del nombre para cubrir dos
  // formatos distintos de nombrado:
  //   a) "Liston Satinado Doble Faz 4cm Verde 30M/Roll" (color embebido)
  //   b) "CINTA SATIN 1.2 CM X 50MTS DOBLE FAZ - KAKI" (color después de " - ")
  // Oct/2026 v4: Resumen por familia — PRIORIZA familia_key como clave de
  // agrupación. Como display, usa el prefijo del nombre antes del PRIMER " - ".
  const prefijoNombre = (nombre) => {
    const n = String(nombre || "").trim();
    const idx = n.indexOf(" - ");
    return (idx > 0 ? n.slice(0, idx) : n).trim().replace(/\s{2,}/g, " ");
  };
  const refKeyDe = (prod) => {
    if (!prod) return "SIN_REF";
    const famKey = String(prod.familia_key || "").trim().toUpperCase();
    return famKey || prefijoNombre(prod.nombre).toUpperCase() || "SIN_REF";
  };
  // Nov/2026: tocar un subtotal → scroll a la familia en el pedido y resaltarla unos segundos.
  const [familiaResaltada, setFamiliaResaltada] = useState(null);
  const familiaResaltadaTimer = useRef(null);
  const irAFamilia = (refKey) => {
    const idx = items.findIndex((it) => refKeyDe(it._prod) === refKey);
    if (idx < 0) return;
    setFamiliaResaltada(refKey);
    if (familiaResaltadaTimer.current) clearTimeout(familiaResaltadaTimer.current);
    familiaResaltadaTimer.current = setTimeout(() => setFamiliaResaltada(null), 3000);
    requestAnimationFrame(() => {
      document.querySelector(`[data-item-idx="${idx}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };
  const subtotalesPorFamilia = useMemo(() => {
    if (items.length === 0) return [];
    const porRef = new Map();
    for (const it of items) {
      const prod = it._prod;
      if (!prod) continue;
      const famKey = String(prod.familia_key || "").trim().toUpperCase();
      const prefijo = prefijoNombre(prod.nombre);
      const refKey = famKey || prefijo.toUpperCase() || "SIN_REF";
      if (!porRef.has(refKey)) {
        porRef.set(refKey, {
          refKey,
          refNombre: (String(prod.nombre || "").includes(" - ") ? prefijo : nombreSinColor(prod)) || prefijo || prod.familia_key || "Sin referencia",
          cantidadTotal: 0,
          subtotalTotal: 0,
        });
      }
      const r = porRef.get(refKey);
      const cant = Number(it.cantidad) || 0;
      const precio = Number(it.precio_unitario) || 0;
      r.cantidadTotal += cant;
      r.subtotalTotal += cant * precio;
    }
    return Array.from(porRef.values());
  }, [items]);

  // ---------- Guardar ----------
  const buildPayload = (forzarBorrador) => {
    const clienteObj = clientes.find((c) => c.id === clienteId);
    const itemsSnap = items.map((it) => ({
      producto_nombre: it._prod?.nombre || "Producto",
      sku: it._prod?.sku,
      cantidad: Number(it.cantidad),
      precio_unitario: Number(it.precio_unitario),
      subtotal: Number(it.cantidad) * Number(it.precio_unitario),
    }));
    const total = itemsSnap.reduce((a, b) => a + b.subtotal, 0);
    return {
      cliente_id: clienteId || null,
      fecha: hoyISO(),
      descuento: 0,
      ...(forzarBorrador ? { forzar_borrador: true } : {}),
      items: items.map((it) => ({
        producto_id: it.producto_id,
        cantidad: Number(it.cantidad),
        precio_unitario: Number(it.precio_unitario),
        motivo_excepcion: null,
        es_surtido: false,
        notas: (it.notas || "").trim() || null,
      })),
      __snapshot: {
        cliente_nombre: clienteObj?.nombre,
        total,
      },
    };
  };

  // Jun/2026: gesto atrás en Android (deslizar desde el borde) sacaba del pedido
  // y se perdían los ítems. Ahora pregunta antes de salir.
  const [exitPrompt, setExitPrompt] = useState(false);
  // Jun/2026: tocar la foto abre la imagen grande (no agrega).
  const [fotoPreview, setFotoPreview] = useState(null);
  const hayCambios = items.length > 0;
  useBackTrap(hayCambios, () => setExitPrompt(true));
  useBeforeUnloadGuard(hayCambios);
  const salirSinGuardar = () => { setExitPrompt(false); navigate("/pedidos"); };
  const guardarYSalir = async () => {
    const ok = await guardarBorrador();
    if (ok) { setExitPrompt(false); navigate("/pedidos"); }
  };

  const guardarBorrador = async () => {
    // Oct/2026: borrador puede ir SIN cliente (vendedor en calle con cliente
    // nuevo). El backend acepta cliente_id=null si forzar_borrador=true. Al
    // confirmar, SÍ exige cliente.
    if (items.length === 0) {
      toast.error("Agrega al menos un producto");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/pedidos", buildPayload(true));
      toast.success(
        `Borrador ${data.numero || "guardado"} ✓${!clienteId ? " (sin cliente — asígnalo antes de confirmar)" : ""}${data.__offline ? " (offline)" : ""}`,
        { duration: 5000 },
      );
      // Reset para seguir con otro cliente
      setItems([]);
      setClienteId("");
      setProdSearch("");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const finalizarPedido = async () => {
    if (!clienteId) {
      toast.error("Selecciona un cliente primero");
      return;
    }
    if (items.length === 0) {
      toast.error("Agrega al menos un producto");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/pedidos", buildPayload(false));
      toast.success(`Pedido ${data.numero || ""} creado ✓`);
      navigate(`/pedidos?abrir=${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo finalizar");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Clientes filtrados ----------
  const clientesFiltrados = useMemo(() => {
    const q = norm(cliSearch);
    if (!q) return clientes.slice(0, 50);
    return clientes
      .filter((c) => {
        const n = norm(c.nombre);
        const t = norm(c.telefono);
        return n.includes(q) || t.includes(q);
      })
      .slice(0, 50);
  }, [cliSearch, clientes]);

  // Productos del historial que se pueden sugerir (match por nombre, pues historial guarda producto_nombre)
  const sugerenciasHistorial = useMemo(() => {
    if (!historial?.productos_top) return [];
    const porNombre = {};
    for (const p of productos) {
      porNombre[norm(p.nombre)] = p;
    }
    return historial.productos_top
      .map((t) => porNombre[norm(t.nombre)])
      .filter(Boolean)
      .slice(0, 8);
  }, [historial, productos]);

  // ---------- UI ----------
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center text-sm text-muted-foreground">
        Cargando catálogo…
      </div>
    );
  }

  // Nov/2026: producto suelto (sin familia o único) — se renderiza en la misma lista que los grupos, por relevancia.
  const renderSuelto = (p) => {
    const disp = (p.stock_actual || 0) - (p.stock_reservado || 0);
    const yaEnCarrito = items.find((it) => it.producto_id === p.id);
    const sinStock = disp <= 0;
    const { sugerido } = pricingFor(p);
    return (
      <button
        key={p.id}
        onClick={() => {
          addProduct(p, 1);
          setProdSearch("");
          searchInputRef.current?.focus();
        }}
        disabled={sinStock}
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-t border-slate-100 dark:border-slate-800 first:border-t-0 ${
          sinStock ? "opacity-50" : "hover:bg-primary/5 active:bg-primary/10"
        }`}
        data-testid={`movil-res-${p.sku || p.id}`}
      >
        <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 overflow-hidden flex-shrink-0 active:ring-2 active:ring-primary" role="button" tabIndex={-1}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setFotoPreview(p); }} data-testid={`movil-foto-${p.id}`}>
          <OfflineImg producto_id={p.id} size={200} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {p.sku && (
              <span className="text-[11px] font-mono font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                {p.sku}
              </span>
            )}
            {p.codigo && (
              <span className="text-[10px] font-mono text-slate-500 truncate">
                {p.codigo}
              </span>
            )}
          </div>
          <div className="text-[12px] sm:text-sm font-medium break-words leading-tight text-slate-900 dark:text-slate-100">
            {p.nombre}
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <span className="font-semibold">{fmt(sugerido)}</span>
            <span className="text-[10px]">·</span>
            <span className={sinStock ? "text-rose-600 font-bold" : ""}>
              {sinStock ? "SIN STOCK" : `${disp} disp`}
            </span>
            {yaEnCarrito && (
              <span className="ml-auto text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">
                +{yaEnCarrito.cantidad} en carrito
              </span>
            )}
          </div>
        </div>
        <Plus className="w-5 h-5 text-primary flex-shrink-0" />
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-40" data-testid="pedido-movil-page">
      {fotoPreview && (
        <div className="fixed inset-0 z-[85] bg-black/80 flex flex-col items-center justify-center p-4" onClick={() => setFotoPreview(null)} data-testid="movil-foto-preview">
          <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-square bg-slate-100 dark:bg-slate-800">
              <OfflineImg producto_id={fotoPreview.id} size={800} className="w-full h-full object-contain" />
            </div>
            <div className="p-3">
              <div className="font-semibold text-sm leading-tight">{fotoPreview.nombre}</div>
              <div className="text-xs text-muted-foreground font-mono">{fotoPreview.codigo || fotoPreview.sku || ""} · disp. {Math.max(0, (fotoPreview.stock_actual || 0) - (fotoPreview.stock_reservado || 0))}</div>
              <div className="mt-3 flex gap-2">
                <button type="button" onClick={() => setFotoPreview(null)} className="flex-1 h-11 rounded-xl border border-border font-semibold" data-testid="movil-foto-cerrar">Cerrar</button>
                <button type="button" disabled={(fotoPreview.stock_actual || 0) - (fotoPreview.stock_reservado || 0) <= 0}
                  onClick={() => { addProduct(fotoPreview, 1); setFotoPreview(null); toast.success(`+1 ${fotoPreview.color || fotoPreview.sku || "producto"} agregado`, { duration: 2000 }); }}
                  className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-semibold disabled:opacity-50" data-testid="movil-foto-agregar">Agregar</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {exitPrompt && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center p-4" data-testid="movil-exit-prompt">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-900 p-5 shadow-xl">
            <div className="font-semibold text-base">¿Salir del pedido?</div>
            <p className="text-sm text-muted-foreground mt-1">Tienes {items.length} artículo{items.length === 1 ? "" : "s"} sin guardar. Si sales sin guardar se pierden.</p>
            <div className="mt-4 flex flex-col gap-2">
              <button type="button" disabled={saving} onClick={guardarYSalir} className="h-11 rounded-xl bg-primary text-primary-foreground font-semibold" data-testid="movil-exit-guardar">Guardar borrador y salir</button>
              <button type="button" onClick={salirSinGuardar} className="h-11 rounded-xl border border-rose-300 text-rose-700 font-semibold" data-testid="movil-exit-descartar">Salir sin guardar</button>
              <button type="button" onClick={() => setExitPrompt(false)} className="h-11 rounded-xl border border-border font-semibold" data-testid="movil-exit-cancelar">Seguir editando</button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-3 py-2 flex items-center gap-2 shadow-sm">
        <button
          onClick={() => (hayCambios ? setExitPrompt(true) : navigate("/pedidos"))}
          className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
          data-testid="movil-back"
          aria-label="Volver"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Modo Vendedor
          </div>
          <div className="text-sm font-bold text-slate-900 dark:text-slate-100 leading-tight">
            Nuevo pedido
          </div>
        </div>
      </div>

      {/* Cliente */}
      <div className="px-3 pt-3">
        <button
          onClick={() => setShowClientePicker(true)}
          className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition ${
            clienteId
              ? "bg-primary/5 border-primary/40"
              : "bg-white dark:bg-slate-900 border-dashed border-slate-300 dark:border-slate-700"
          }`}
          data-testid="movil-cliente-selector"
        >
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              clienteId ? "bg-primary text-primary-foreground" : "bg-slate-100 dark:bg-slate-800 text-slate-400"
            }`}
          >
            <User className="w-5 h-5" />
          </div>
          <div className="flex-1 text-left min-w-0">
            {clienteId && cliente ? (
              <>
                <div className="font-semibold text-sm truncate">{cliente.nombre}</div>
                <div className="text-xs text-slate-500 truncate">
                  {cliente.telefono || cliente.ciudad || "Cliente"}
                </div>
              </>
            ) : (
              <>
                <div className="font-semibold text-sm text-slate-500">Selecciona cliente</div>
                <div className="text-xs text-slate-400">Toca aquí para elegir</div>
              </>
            )}
          </div>
          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
        </button>

        {/* Historial compacto */}
        {clienteId && sugerenciasHistorial.length > 0 && (
          <button
            onClick={() => setShowHistorial(true)}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-900 dark:text-amber-200 text-xs font-medium"
            data-testid="movil-ver-historial"
          >
            <History className="w-3.5 h-3.5" />
            <span>Suele pedir {sugerenciasHistorial.length} productos — ver historial</span>
          </button>
        )}
      </div>

      {/* Buscador */}
      <div className="px-3 pt-3">
        <div className="relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            ref={searchInputRef}
            type="text"
            inputMode="search"
            autoComplete="off"
            placeholder="SKU, código o nombre…"
            value={prodSearch}
            onChange={(e) => setProdSearch(e.target.value)}
            className="w-full pl-11 pr-24 py-3.5 text-base rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
            data-testid="movil-buscador"
          />
          <button
            onClick={startListening}
            className={`absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-lg flex items-center justify-center ${
              listening
                ? "bg-rose-500 text-white animate-pulse"
                : "bg-primary/10 text-primary"
            }`}
            data-testid="movil-voz-btn"
            aria-label="Dictar por voz"
          >
            {listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
          {prodSearch && (
            <button
              onClick={() => {
                setProdSearch("");
                searchInputRef.current?.focus();
              }}
              className="absolute right-14 top-1/2 -translate-y-1/2 p-1"
              data-testid="movil-limpiar-busqueda"
              aria-label="Limpiar"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
        {!loading && productos.length === 0 && (
          <div className="mt-2 rounded-xl border border-rose-300 bg-rose-50 text-rose-700 text-xs px-3 py-2 flex items-center justify-between gap-2" data-testid="movil-catalogo-error">
            <span>No hay productos cargados{catalogoError ? ` (${catalogoError})` : ""}. Sin catálogo el buscador no funciona.</span>
            <button type="button" onClick={cargarCatalogo} className="font-bold underline whitespace-nowrap" data-testid="movil-catalogo-reintentar">Reintentar</button>
          </div>
        )}
      </div>

      {/* Botón de pedido inteligente — SIEMPRE disponible para agregar más items.
          Feb/2026: antes se escondía cuando había resultados de búsqueda,
          dejando al vendedor sin acceso al parser IA. Ahora siempre visible. */}
      <div className="px-3 mt-3">
        <button
          onClick={() => setShowIntel(true)}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-primary to-fuchsia-500 text-white font-bold text-sm shadow-lg flex items-center justify-center gap-2 hover:opacity-95 active:scale-[0.98] transition"
          data-testid="movil-intel-btn"
        >
          <Sparkles className="w-5 h-5" />
          {items.length === 0 ? "Montar con IA (texto o foto)" : "Agregar más con IA"}
        </button>
      </div>

      {/* Resultados de búsqueda (agrupados por familia + sueltos) */}
      {tieneResultados && (
        <div className="px-3 mt-2" data-testid="movil-resultados">
          <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 overflow-hidden max-h-[55vh] overflow-y-auto">
            {/* Grupos / Familias */}
            {resultados.lista.map((entrada) => {
              if (entrada.tipo === "suelto") return renderSuelto(entrada.p);
              const grp = entrada;
              const expandido = gruposExpandidos.has(grp.key);
              const sinStock = grp.stockTotal <= 0;
              // Oct/2026: cuántas variantes del grupo ya están en el carrito
              // (y cuántas unidades totales) — se muestra en el header y en
              // la barra sticky inferior cuando el grupo está expandido.
              const variantesEnCarrito = grp.variantes.filter((v) =>
                items.find((it) => it.producto_id === v.id),
              );
              const unidadesDelGrupo = variantesEnCarrito.reduce((s, v) => {
                const it = items.find((x) => x.producto_id === v.id);
                return s + (it?.cantidad || 0);
              }, 0);
              return (
                <div
                  key={`grp-${grp.key}`}
                  className="border-b border-slate-100 dark:border-slate-800 last:border-b-0"
                  data-testid={`movil-grupo-${grp.key}`}
                >
                  {/* Header del grupo */}
                  <button
                    onClick={() => toggleGrupo(grp.key)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left ${sinStock ? "opacity-60" : "hover:bg-primary/5 active:bg-primary/10"}`}
                    data-testid={`movil-grupo-toggle-${grp.key}`}
                  >
                    <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 overflow-hidden flex-shrink-0 ring-2 ring-primary/20">
                      <OfflineImg producto_id={grp.thumb.id} size={200} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                          GRUPO
                        </span>
                        {variantesEnCarrito.length > 0 && (
                          <span className="text-[9px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded">
                            <Check className="w-3 h-3 inline -mt-0.5 mr-0.5" />{variantesEnCarrito.length} agregadas · {unidadesDelGrupo}u
                          </span>
                        )}
                        {grp.codigo && !variantesEnCarrito.length && (
                          <span className="text-[10px] font-mono text-slate-500 truncate">
                            {grp.codigo}
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] sm:text-sm font-semibold break-words leading-tight text-slate-900 dark:text-slate-100">
                        {grp.nombre}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-2">
                        <span className="font-semibold">{grp.variantes.length} variantes</span>
                        <span className="text-[10px]">·</span>
                        <span className={sinStock ? "text-rose-600 font-bold" : ""}>
                          {sinStock ? "SIN STOCK" : `${grp.stockTotal} disp total`}
                        </span>
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${expandido ? "rotate-180" : ""}`}
                    />
                  </button>
                  {/* Variantes expandidas */}
                  {expandido && (
                    <div className="bg-slate-50 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-800">
                      {grp.variantes.map((v) => {
                        const disp = (v.stock_actual || 0) - (v.stock_reservado || 0);
                        const sinStockV = disp <= 0;
                        const yaEnCarrito = items.find((it) => it.producto_id === v.id);
                        const { sugerido } = pricingFor(v);
                        // Feb/2026: fallback mejorado — cuando v.color está vacío,
                        // en vez de mostrar "—" (ilegible para el vendedor), usamos
                        // el nombre quitándole el prefijo de la familia. Si tampoco
                        // podemos extraer un sufijo, mostramos el nombre completo.
                        const nombreV = (v.nombre || "").trim();
                        const famPrefix = (grp.familia || "").trim();
                        let colorFallback = nombreV;
                        if (famPrefix && nombreV.toLowerCase().startsWith(famPrefix.toLowerCase())) {
                          const resto = nombreV.slice(famPrefix.length).trim();
                          if (resto) colorFallback = resto;
                        }
                        const color = v.color || (nombreV.includes(" - ") ? nombreV.split(" - ").slice(1).join(" - ") : "") || colorFallback || "Variante";
                        const qtyInput = qtyPorVariante[v.id] ?? "";
                        const qtyNum = Math.max(1, parseInt(qtyInput || "1", 10) || 1);
                        const agregar = () => {
                          addProduct(v, qtyNum);
                          setQtyPorVariante((p) => ({ ...p, [v.id]: "" })); // reset tras agregar
                          toast.success(`+${qtyNum} ${v.color || v.sku || "producto"} agregado`, { duration: 2000 });
                        };
                        return (
                          <div
                            key={v.id}
                            className={`flex items-center gap-2 px-3 py-2 pl-5 border-t border-slate-100 dark:border-slate-800 ${sinStockV ? "opacity-50" : ""}`}
                            data-testid={`movil-grupo-var-${v.sku || v.id}`}
                          >
                            <div className="w-9 h-9 rounded-md bg-white dark:bg-slate-800 overflow-hidden flex-shrink-0 active:ring-2 active:ring-primary" role="button" tabIndex={-1}
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setFotoPreview(v); }} data-testid={`movil-foto-${v.id}`}>
                              <OfflineImg producto_id={v.id} size={200} className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline gap-1.5">
                                <span className="text-sm font-bold text-slate-900 dark:text-slate-100 block truncate" title={color}>{color}</span>
                              </div>
                              <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                                <span className="font-semibold">{fmt(sugerido)}</span>
                                <span>·</span>
                                <span className={sinStockV ? "text-rose-600 font-bold" : ""}>
                                  {sinStockV ? "SIN STOCK" : `${disp} disp`}
                                </span>
                                {yaEnCarrito && (
                                  <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded font-bold">
                                    +{yaEnCarrito.cantidad}
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* Input de cantidad + botón Agregar */}
                            <input
                              type="number"
                              inputMode="numeric"
                              placeholder="1"
                              value={qtyInput}
                              onChange={(e) => {
                                const v2 = e.target.value.replace(/[^\d]/g, "");
                                setQtyPorVariante((p) => ({ ...p, [v.id]: v2 }));
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !sinStockV) {
                                  e.preventDefault();
                                  agregar();
                                }
                              }}
                              disabled={sinStockV}
                              className="w-14 h-9 text-center text-sm font-bold bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 flex-shrink-0"
                              data-testid={`movil-grupo-var-qty-${v.sku || v.id}`}
                              aria-label="Cantidad"
                            />
                            <button
                              onClick={agregar}
                              disabled={sinStockV}
                              className="h-9 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-bold flex items-center gap-1 active:scale-95 disabled:opacity-30 flex-shrink-0"
                              data-testid={`movil-grupo-var-add-${v.sku || v.id}`}
                            >
                              <Plus className="w-3.5 h-3.5" />
                              {qtyInput && qtyNum > 1 ? qtyNum : "Agregar"}
                            </button>
                          </div>
                        );
                      })}
                      {/* Oct/2026: footer del grupo con contador + Listo
                          — da feedback claro al vendedor de lo que agregó y
                          cierra el grupo con un tap. */}
                      {variantesEnCarrito.length > 0 && (
                        <div className="sticky bottom-0 flex items-center gap-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/40 border-t border-emerald-200 dark:border-emerald-800">
                          <Check className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                          <div className="flex-1 min-w-0 text-xs text-emerald-900 dark:text-emerald-100">
                            <span className="font-bold">{variantesEnCarrito.length} variantes</span>
                            <span className="opacity-70"> · </span>
                            <span className="font-bold tabular-nums">{unidadesDelGrupo} unidades</span>
                            <span className="opacity-70"> agregadas al pedido</span>
                          </div>
                          <button
                            onClick={() => toggleGrupo(grp.key)}
                            className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1 active:scale-95 flex-shrink-0"
                            data-testid={`movil-grupo-listo-${grp.key}`}
                          >
                            Listo
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Carrito */}
      <div className="px-3 mt-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500">
            Carrito ({items.length})
          </h2>
          {items.length > 0 && (
            <button
              onClick={() => {
                if (confirm("¿Vaciar el carrito?")) setItems([]);
              }}
              className="text-xs text-rose-600 font-semibold"
              data-testid="movil-vaciar"
            >
              Vaciar
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <div className="text-center py-8 bg-white dark:bg-slate-900 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
            <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <div className="text-sm text-slate-500">
              Busca o dicta un SKU para empezar
            </div>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="movil-carrito">
            {items.map((it, idx) => {
              const p = it._prod;
              const esUltimo = ultimoAgregado && it.producto_id === ultimoAgregado;
              const famActual = familiaDe(it);
              const nuevaFamilia = idx === 0 || familiaDe(items[idx - 1]) !== famActual;
              const disp = (p?.stock_actual || 0) - (p?.stock_reservado || 0);
              const bajoStock = it.cantidad > disp && disp >= 0;
              const prM = p ? pricingFor(p) : { sugerido: 0, minimo: 0 };
              const bajoMin = prM.minimo > 0 && Number(it.precio_unitario) < prM.minimo;
              const enFamiliaResaltada = familiaResaltada && refKeyDe(p) === familiaResaltada;
              const setPrecio = (n) => setItems((prev) => { const next = [...prev]; next[idx] = { ...next[idx], precio_unitario: n }; return next; });
              return (
                <li
                  key={`${it.producto_id}-${idx}`}
                  className={`rounded-xl border p-3 relative transition-colors ${enFamiliaResaltada ? "bg-primary/10 border-primary ring-2 ring-primary/60" : bajoMin ? "bg-rose-50 border-rose-300 border-l-4 border-l-rose-500 dark:bg-rose-950/30" : esUltimo ? "bg-primary/5 border-primary ring-2 ring-primary/30 dark:bg-primary/10" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"}`}
                  data-item-idx={idx}
                  data-familia-resaltada={enFamiliaResaltada ? "1" : undefined}
                  data-bajo-minimo={bajoMin ? "1" : undefined}
                  data-pid={it.producto_id}
                  data-recien-agregado={esUltimo ? "1" : undefined}
                  data-testid={`movil-item-${p?.sku || idx}`}
                >
                  {nuevaFamilia && famActual && !famActual.startsWith("__") && (
                    <div className="-mt-1 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 truncate" data-testid="movil-grupo-label">{famActual}</div>
                  )}
                  {esUltimo && <span className="absolute -top-2 right-3 text-[9px] font-bold uppercase tracking-wider bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full" data-testid="movil-recien-agregado">Recién agregado</span>}
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 overflow-hidden flex-shrink-0">
                      <OfflineImg producto_id={it.producto_id} size={200} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        {p?.sku && (
                          <span className="text-[11px] font-mono font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                            {p.sku}
                          </span>
                        )}
                        {p?.codigo && (
                          <span className="text-[10px] font-mono text-slate-500 truncate">
                            {p.codigo}
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] sm:text-sm font-medium text-slate-900 dark:text-slate-100 break-words leading-tight">
                        {p?.nombre || "Producto"}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-1 flex-wrap">
                        <button
                          type="button"
                          onClick={() => {
                            const nuevo = prompt(`Precio por unidad (actual $${it.precio_unitario})`, String(it.precio_unitario || 0));
                            if (nuevo === null) return;
                            const n = Math.max(0, parseInt(String(nuevo).replace(/[^\d]/g, ""), 10) || 0);
                            setItems((prev) => {
                              const next = [...prev];
                              next[idx] = { ...next[idx], precio_unitario: n };
                              return next;
                            });
                          }}
                          className="font-semibold text-primary underline-offset-2 hover:underline"
                          data-testid={`movil-item-precio-${idx}`}
                          title="Toca para editar"
                        >
                          {fmt(it.precio_unitario)} c/u
                        </button>
                        <span>·</span>
                        <span>{fmt(it.cantidad * it.precio_unitario)} total</span>
                      </div>
                      {prM.minimo > 0 && (
                        <div className="text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap mt-0.5">
                          <span>Usar:</span>
                          <button type="button" onClick={() => setPrecio(prM.sugerido)} className="underline decoration-dotted underline-offset-2" data-testid={`movil-item-usar-sug-${idx}`}>sug {fmt(prM.sugerido)}</button>
                          <span>·</span>
                          <button type="button" onClick={() => setPrecio(prM.minimo)} className={`underline decoration-dotted underline-offset-2 ${bajoMin ? "text-rose-700 font-bold" : ""}`} data-testid={`movil-item-usar-min-${idx}`}>mín {fmt(prM.minimo)}</button>
                          {bajoMin && <span className="text-rose-700 font-semibold"><AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-1" />bajo mínimo — requiere aprobación</span>}
                        </div>
                      )}
                      {bajoStock && (
                        <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-700 font-semibold">
                          <AlertTriangle className="w-3 h-3" />
                          Pediste {it.cantidad} y hay {disp} disp
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setItems((prev) => { const next = [...prev]; next[idx] = { ...next[idx], _notaAbierta: !next[idx]._notaAbierta }; return next; })}
                      className={`p-1.5 -mt-1 rounded-lg ${it.notas ? "text-amber-600" : "text-slate-400 hover:text-amber-600"}`}
                      data-testid={`movil-item-nota-btn-${idx}`}
                      aria-label="Nota"
                      title="Nota para bodega (ej. recargo en dorado)"
                    >
                      <StickyNote className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => removeItem(idx)}
                      className="p-1.5 -mt-1 -mr-1 rounded-lg hover:bg-rose-50 text-rose-500"
                      data-testid={`movil-item-del-${idx}`}
                      aria-label="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {(it._notaAbierta || it.notas) && (
                    <div className="mt-2 flex items-center gap-2" data-testid={`movil-item-nota-row-${idx}`}>
                      <StickyNote className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                      <input
                        type="text"
                        value={it.notas || ""}
                        onChange={(e) => setItems((prev) => { const next = [...prev]; next[idx] = { ...next[idx], notas: e.target.value }; return next; })}
                        className="flex-1 h-9 rounded-lg border border-amber-200 bg-amber-50/60 px-2 text-sm"
                        placeholder="Nota para bodega / remisión (ej. Rojo 2, Rosa 2 · recargo en dorado)"
                        data-testid={`movil-item-nota-input-${idx}`}
                      />
                    </div>
                  )}
                  {/* Botones -/+ gigantes — 2 filas para no overflow en móviles angostos */}
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQty(idx, -1)}
                        className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center active:scale-95"
                        data-testid={`movil-item-minus-${idx}`}
                        aria-label="Restar"
                      >
                        <Minus className="w-5 h-5" />
                      </button>
                      <input
                        type="number"
                        inputMode="numeric"
                        value={it.cantidad}
                        onChange={(e) => {
                          const v = Math.max(0, parseInt(e.target.value || "0", 10));
                          setItems((prev) => {
                            const next = [...prev];
                            next[idx] = { ...next[idx], cantidad: v };
                            return next;
                          });
                        }}
                        className="flex-1 h-12 text-center text-lg font-bold bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-primary"
                        data-testid={`movil-item-qty-${idx}`}
                      />
                      <button
                        onClick={() => updateQty(idx, 1)}
                        className="w-12 h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center active:scale-95 shadow-sm"
                        data-testid={`movil-item-plus-${idx}`}
                        aria-label="Sumar"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                    {/* Fila de aceleradores — grid full-width, no overflow */}
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => updateQty(idx, 6)}
                        className="h-9 rounded-lg bg-primary/10 text-primary text-xs font-bold active:scale-95"
                        data-testid={`movil-item-plus6-${idx}`}
                      >
                        +6
                      </button>
                      <button
                        onClick={() => updateQty(idx, 12)}
                        className="h-9 rounded-lg bg-primary/10 text-primary text-xs font-bold active:scale-95"
                        data-testid={`movil-item-plus12-${idx}`}
                      >
                        +12
                      </button>
                      <button
                        onClick={() => updateQty(idx, 24)}
                        className="h-9 rounded-lg bg-primary/10 text-primary text-xs font-bold active:scale-95"
                        data-testid={`movil-item-plus24-${idx}`}
                      >
                        +24
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Subtotales por familia (Feb/2026) — resumen plano, sin desglose. */}
        {subtotalesPorFamilia.length > 0 && (
          <div className="mt-4 rounded-xl border-2 border-primary/25 bg-primary/5 p-3" data-testid="movil-subtotales-ref">
            <div className="text-[10px] font-bold uppercase tracking-wider text-primary mb-2 flex items-center justify-between">
              <span>Resumen por familia</span>
              <span className="text-[9px] opacity-60">v2</span>
            </div>
            <div className="space-y-1.5">
              {subtotalesPorFamilia.map((r, i) => (
                <button type="button" key={i} onClick={() => irAFamilia(r.refKey)}
                  data-testid={`movil-subtotal-familia-${i}`}
                  className={`w-full text-left bg-white dark:bg-slate-900 rounded-lg p-2 border flex items-baseline justify-between gap-2 active:bg-primary/10 ${familiaResaltada === r.refKey ? "border-primary ring-2 ring-primary/40" : "border-primary/15"}`}>
                  <span className="font-semibold text-xs break-words min-w-0" title={r.refNombre}>↑ {r.refNombre}</span>
                  <span className="text-xs font-bold tabular-nums text-primary flex-shrink-0">
                    {r.cantidadTotal} un · {fmt(r.subtotalTotal)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Barra flotante inferior */}
      <div
        className="fixed bottom-0 inset-x-0 z-30 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 8px)" }}
        data-testid="movil-footer"
      >
        <div className="px-3 pt-2.5 pb-2 flex items-baseline justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Total ({cantidadTotal} unid)
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {fmt(subtotal)}
            </div>
          </div>
          {cliente && (
            <div className="text-right max-w-[45%]">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Cliente
              </div>
              <div className="text-sm font-medium truncate">{cliente.nombre}</div>
            </div>
          )}
        </div>
        <div className="px-3 pb-3 grid grid-cols-2 gap-2">
          <button
            onClick={guardarBorrador}
            disabled={saving || items.length === 0}
            className="h-12 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40"
            data-testid="movil-btn-borrador"
          >
            <Save className="w-4 h-4" />
            Guardar borrador
          </button>
          <button
            onClick={finalizarPedido}
            disabled={saving || !clienteId || items.length === 0}
            className="h-12 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40 active:scale-95"
            data-testid="movil-btn-finalizar"
          >
            <Check className="w-4 h-4" />
            Finalizar
          </button>
        </div>
      </div>

      {/* Picker de cliente */}
      {showClientePicker && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowClientePicker(false)}
          data-testid="movil-cliente-picker"
        >
          <div
            className="bg-white dark:bg-slate-900 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
              <input
                autoFocus
                type="text"
                placeholder="Buscar cliente…"
                value={cliSearch}
                onChange={(e) => setCliSearch(e.target.value)}
                className="flex-1 h-11 px-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary"
                data-testid="movil-cliente-search"
              />
              <button
                onClick={() => setShowClientePicker(false)}
                className="p-2"
                aria-label="Cerrar"
                data-testid="movil-cliente-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {clientesFiltrados.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500">
                  Sin resultados
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {clientesFiltrados.map((c) => (
                    <li key={c.id}>
                      <button
                        onClick={() => {
                          setClienteId(c.id);
                          setShowClientePicker(false);
                          setCliSearch("");
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-primary/5 active:bg-primary/10"
                        data-testid={`movil-cli-${c.id}`}
                      >
                        <div className="font-semibold text-sm">{c.nombre}</div>
                        <div className="text-xs text-slate-500">
                          {[c.telefono, c.ciudad].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Drawer: historial del cliente */}
      {showHistorial && historial && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center"
          onClick={() => setShowHistorial(false)}
          data-testid="movil-historial-drawer"
        >
          <div
            className="bg-white dark:bg-slate-900 w-full max-h-[80vh] rounded-t-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 border-b border-slate-200 dark:border-slate-800 flex items-center">
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Historial — {cliente?.nombre}
                </div>
                <div className="text-sm font-bold">
                  Suele pedir estos productos
                </div>
              </div>
              <button
                onClick={() => setShowHistorial(false)}
                className="p-2"
                aria-label="Cerrar"
                data-testid="movil-historial-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {sugerenciasHistorial.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-8">
                  Sin historial reciente
                </div>
              ) : (
                sugerenciasHistorial.map((p) => {
                  const disp = (p.stock_actual || 0) - (p.stock_reservado || 0);
                  return (
                    <button
                      key={p.id}
                      onClick={() => {
                        addProduct(p, 1);
                        toast.success(`+1 ${p.sku || p.nombre}`);
                      }}
                      disabled={disp <= 0}
                      className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 disabled:opacity-50 active:bg-primary/5"
                      data-testid={`movil-hist-${p.sku || p.id}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-white dark:bg-slate-900 overflow-hidden flex-shrink-0">
                        <OfflineImg producto_id={p.id} size={200} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center gap-1.5">
                          {p.sku && (
                            <span className="text-[11px] font-mono font-bold text-primary">
                              {p.sku}
                            </span>
                          )}
                        </div>
                        <div className="text-[12px] sm:text-sm font-medium break-words leading-tight">{p.nombre}</div>
                        <div className="text-xs text-slate-500">
                          {disp <= 0 ? "Sin stock" : `${disp} disp`}
                        </div>
                      </div>
                      <Plus className="w-5 h-5 text-primary flex-shrink-0" />
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dialog de pedido inteligente (IA parsea WhatsApp / foto remisión) */}
      <IntelligentOrderDialog
        open={showIntel}
        onClose={() => setShowIntel(false)}
        productos={productos}
        onConfirmar={(parsed) => {
          // Oct/2026 bug fix v2: SUMAR cantidades para producto_ids duplicados
          // en vez de descartar. UNA sola llamada funcional a setItems.
          const porPid = new Map();
          let sinPid = 0;
          for (const row of parsed) {
            const pid = row.producto?.producto_id;
            if (!pid) { sinPid++; continue; }
            const cant = Number(row.cantidad || 0);
            if (!porPid.has(pid)) porPid.set(pid, { row, cantidadTotal: 0 });
            porPid.get(pid).cantidadTotal += cant;
          }
          const bumpeados = new Map();
          const nuevos = [];
          for (const [pid, info] of porPid) {
            const existe = items.some((it) => it.producto_id === pid);
            if (existe) { bumpeados.set(pid, info.cantidadTotal); continue; }
            const p = info.row.producto;
            const full = productos.find((pp) => pp.id === pid);
            const target = full || {
              id: pid, sku: p.sku, nombre: p.nombre,
              stock_actual: p.disponible, stock_reservado: 0,
              precio_sugerido: info.row.precio_unitario_ia || p.precio_sugerido,
              imagen_path: p.imagen_path, unidad: p.unidad,
              _permitir_sin_stock: true,
              _precio_override: info.row.precio_unitario_ia || null,
            };
            if (info.row.precio_unitario_ia) {
              target.precio_sugerido = info.row.precio_unitario_ia;
              target._precio_override = info.row.precio_unitario_ia;
            }
            const { sugerido } = pricingFor(target);
            nuevos.push({
              producto_id: pid,
              cantidad: info.cantidadTotal,
              precio_unitario: info.row.precio_unitario_ia || sugerido,
              _prod: target,
            });
          }
          if (bumpeados.size > 0 || nuevos.length > 0) {
            setItems((prev) => {
              const updated = prev.map((it) =>
                bumpeados.has(it.producto_id)
                  ? { ...it, cantidad: (Number(it.cantidad) || 0) + bumpeados.get(it.producto_id) }
                  : it
              );
              return agruparCarrito([...updated, ...nuevos]);
            });
            if (nuevos.length) setUltimoAgregado(nuevos[nuevos.length - 1].producto_id);
          }
          const partes = [];
          if (nuevos.length) partes.push(`${nuevos.length} nuevo${nuevos.length === 1 ? "" : "s"}`);
          if (bumpeados.size) partes.push(`${bumpeados.size} con cantidad sumada`);
          if (sinPid) partes.push(`${sinPid} sin resolver`);
          if (nuevos.length === 0 && bumpeados.size === 0) {
            toast.info("No se agregó nada al pedido");
          } else {
            toast.success(`IA: ${partes.join(" · ")}`);
          }
        }}
      />

      {/* Oct/2026: FAB para pedidos largos en móvil — flota siempre que el
          pedido tenga >=5 items, scrolea al buscador y lo enfoca.
          Posicionado arriba del footer total/finalizar (bottom-28). */}
      {items.length >= 5 && (
        <button
          type="button"
          onClick={() => {
            if (searchInputRef.current) {
              searchInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
              setTimeout(() => searchInputRef.current?.focus(), 400);
            }
          }}
          className="fixed bottom-28 right-5 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-2xl hover:scale-105 active:scale-95 transition flex items-center justify-center ring-4 ring-primary/20"
          title={`Buscar y agregar (${items.length} items)`}
          data-testid="movil-fab-search"
        >
          <Search className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
